import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, useSlots, watch } from 'vue';
import { BTable } from 'bootstrap-vue';
import { isEqual } from 'underscore';

interface GenericItem {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MultiSelectTable',
  props: {
    value: null,
    items: { default: () => [] },
    title: { default: 'Select one or more items' },
    showDoneButton: { type: Boolean, default: true },
    doneButtonText: { default: 'Done' },
    fields: { default: () => [] },
    loading: { type: Boolean },
    itemsPerPage: null,
    totalCount: null,
    requiredSelectedItems: { default: 0 },
    idKey: { default: 'id' },
    maxSelections: { default: 0 }
  },
  emits: [
    'input',
    'search',
    'row-clicked',
    'item-selected',
    'selection-finished',
    'details-visibility-change'
],
  setup(__props: any, { emit }) {

const props = __props as { value?: GenericItem[] | string[], items: GenericItem[], title: string, showDoneButton: boolean, doneButtonText: string, fields: Record<string, string[]>[], loading?: boolean, itemsPerPage?: number, totalCount?: number, requiredSelectedItems: number, idKey: string, maxSelections: number };





const slots = useSlots();

const currentPage = ref(1);
const searchString = ref('');
let internalSelectedItems: GenericItem[] = [];
let internalSelectedIds: (string | number)[] = [];

const selectedItems = computed({
    get: () => props.value,
    set: (value) => emit('input', value)
});

const hasDetailsView = computed((): boolean => {
    return !!slots['row-details'];
});

const canClickDone = computed((): boolean => {
    return (selectedItems.value?.length || 0) >= props.requiredSelectedItems;
});

// When multi-page is active, the selected items are stored in an array of ids instead of an array of items
const isMultiPage = computed((): boolean => {
    return typeof props.itemsPerPage === 'number';
});

const selectedItemsCount = computed((): number => {
    if (isMultiPage.value) {
        return internalSelectedIds.length;
    } else {
        return internalSelectedItems.length;
    }
});

watch([searchString, currentPage], () => {
    dispatchSearch();
});

watch([selectedItems, props.items], () => {
    refreshSelectedRows();
});

onMounted(() => {
    refreshSelectedRows();
    dispatchSearch();
});

const dispatchSearch = () => {
    if (props.itemsPerPage) {
        emit('search', {
            search: searchString.value,
            from: (currentPage.value - 1) * props.itemsPerPage,
            count: props.itemsPerPage
        });
    }
};

const getTrClass = (item: GenericItem | null) => {
    if (
        item && isMultiPage.value
            ? internalSelectedIds.some((id) => id == item[props.idKey])
            : internalSelectedItems.some((i) => isEqual(i, item))
    ) {
        return ['b-table-row-selected', 'table-active'];
    } else {
        return [];
    }
};

const getTrAttr = (item: GenericItem | null) => {
    if (
        item && isMultiPage.value
            ? internalSelectedIds.some((id) => id == item[props.idKey])
            : internalSelectedItems.some((i) => isEqual(i, item))
    ) {
        return {
            'aria-selected': true
        };
    } else {
        return {
            'aria-selected': false
        };
    }
};

const onRowClicked = (clickedItem: GenericItem) => {
    if (hasDetailsView.value) {
        return;
    }

    let itemSelected = false;
    const index = isMultiPage.value
        ? internalSelectedIds.findIndex((i) => i == clickedItem[props.idKey])
        : internalSelectedItems.findIndex((i) => isEqual(i, clickedItem));

    if (index > -1) {
        // Remove the already selected item
        if (isMultiPage.value) {
            internalSelectedIds.splice(index, 1);
        } else {
            internalSelectedItems.splice(index, 1);
        }
    } else {
        // Add the selected item
        if (
            isMultiPage.value &&
            (props.maxSelections === 0 || (props.maxSelections > 0 && selectedItemsCount.value < props.maxSelections))
        ) {
            itemSelected = true;
            internalSelectedIds.push(clickedItem[props.idKey]);
        } else if (
            !isMultiPage.value &&
            (props.maxSelections === 0 || (props.maxSelections > 0 && selectedItemsCount.value < props.maxSelections))
        ) {
            itemSelected = true;
            internalSelectedItems.push(clickedItem);
        }
    }

    emit('input', isMultiPage.value ? internalSelectedIds : internalSelectedItems);
    emit('row-clicked', clickedItem);

    if (itemSelected) {
        emit('item-selected', clickedItem);
    }
};

const onDone = () => {
    emit('selection-finished', selectedItems.value);
};

const refreshSelectedRows = () => {
    if (selectedItems.value && selectedItems.value.length && props.items) {
        if (isMultiPage.value) {
            internalSelectedIds = selectedItems.value as string[];
        } else {
            internalSelectedItems = selectedItems.value as GenericItem[];
        }
    }
};

return { __sfc: true,props, emit, slots, currentPage, searchString, internalSelectedItems, internalSelectedIds, selectedItems, hasDetailsView, canClickDone, isMultiPage, selectedItemsCount, dispatchSearch, getTrClass, getTrAttr, onRowClicked, onDone, refreshSelectedRows, BTable }
}

})